import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { PermissionSelector } from '@/components/menu/PermissionSelector';
import {
  navigationIconOptions,
  navigationOpenModeOptions,
  navigationVisibilityOptions,
} from '@/config/navigationConfig';
import { navigationStudio } from '@/constants/navigationStudio';
import type { NavigationNode } from '@/types/navigationConfig';
import { resolveNodeLabel } from '@/utils/navigationDisplayUtils';

export interface NavigationPropertiesFormValues {
  label: string;
  route: string;
  permission: string;
  keywords: string;
  tooltip: string;
  icon: string;
  visibility: NavigationNode['visibility'];
  displayOrder: number;
  openMode: NonNullable<NavigationNode['openMode']>;
  hidden: boolean;
  disabled: boolean;
}

interface NavigationPropertiesProps {
  node: NavigationNode | undefined;
  siblingLabels: string[];
  allRoutes: string[];
  isLoading?: boolean;
  onChange: (updates: Partial<NavigationNode>) => void;
}

export function NavigationProperties({
  node,
  siblingLabels,
  allRoutes,
  isLoading = false,
  onChange,
}: NavigationPropertiesProps) {
  const { t } = useTranslation('entity-portal');
  const nodeRef = useRef<NavigationNode | undefined>(node);
  const siblingLabelsRef = useRef<string[]>(siblingLabels);
  const allRoutesRef = useRef<string[]>(allRoutes);
  const onChangeRef = useRef(onChange);

  const schema = useMemo(
    () =>
      yup
        .object({
          label: yup
            .string()
            .trim()
            .required(t('navigationConfig.validation.labelRequired')),
          route: yup
            .string()
            .default('')
            .when('$nodeType', {
              is: 'item',
              then: (fieldSchema) =>
                fieldSchema
                  .trim()
                  .required(t('navigationConfig.validation.routeRequired')),
              otherwise: (fieldSchema) => fieldSchema,
            }),
          permission: yup.string().default('standard-user'),
          keywords: yup.string().default(''),
          tooltip: yup.string().default(''),
          icon: yup.string().default('apps'),
          visibility: yup
            .mixed<NavigationNode['visibility']>()
            .oneOf(['visible', 'hidden', 'disabled'])
            .required(),
          displayOrder: yup.number().min(0).required(),
          openMode: yup
            .mixed<NonNullable<NavigationNode['openMode']>>()
            .oneOf(['sameTab', 'newTab'])
            .required(),
          hidden: yup.boolean().default(false),
          disabled: yup.boolean().default(false),
        })
        .required(),
    [t],
  );

  const defaultValues = useMemo<NavigationPropertiesFormValues>(
    () => ({
      label: node ? resolveNodeLabel(node.label, t) : '',
      route: node?.route ?? '',
      permission: node?.permissions?.[0] ?? 'standard-user',
      keywords: node?.keywords?.join(', ') ?? '',
      tooltip: node?.tooltip ? resolveNodeLabel(node.tooltip, t) : '',
      icon: node?.icon ?? 'apps',
      visibility: node?.visibility ?? 'visible',
      displayOrder: node?.displayOrder ?? 1,
      openMode: node?.openMode ?? 'sameTab',
      hidden: node?.visibility === 'hidden',
      disabled: node?.visibility === 'disabled',
    }),
    [node, t],
  );

  const {
    register,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<NavigationPropertiesFormValues>({
    resolver: yupResolver(schema),
    context: { nodeType: node?.type ?? 'item' },
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    nodeRef.current = node;
    siblingLabelsRef.current = siblingLabels;
    allRoutesRef.current = allRoutes;
    onChangeRef.current = onChange;
  }, [allRoutes, node, onChange, siblingLabels]);

  useEffect(() => {
    const currentNode = nodeRef.current;

    if (!currentNode) {
      return;
    }

    reset({
      label: resolveNodeLabel(currentNode.label, t),
      route: currentNode.route ?? '',
      permission: currentNode.permissions?.[0] ?? 'standard-user',
      keywords: currentNode.keywords?.join(', ') ?? '',
      tooltip: currentNode.tooltip
        ? resolveNodeLabel(currentNode.tooltip, t)
        : '',
      icon: currentNode.icon ?? 'apps',
      visibility: currentNode.visibility,
      displayOrder: currentNode.displayOrder,
      openMode: currentNode.openMode ?? 'sameTab',
      hidden: currentNode.visibility === 'hidden',
      disabled: currentNode.visibility === 'disabled',
    });
  }, [node?.id, reset, t]);

  useEffect(() => {
    const subscription = watch((values) => {
      const currentNode = nodeRef.current;

      if (!currentNode || !values.label?.trim()) {
        return;
      }

      const trimmedLabel = values.label.trim();
      const trimmedRoute = values.route?.trim() ?? '';
      const duplicateLabel = siblingLabelsRef.current.some(
        (label) => label.toLowerCase() === trimmedLabel.toLowerCase(),
      );
      const duplicateRoute =
        currentNode.type === 'item' &&
        trimmedRoute &&
        allRoutesRef.current.some(
          (route) =>
            route.toLowerCase() === trimmedRoute.toLowerCase() &&
            route !== currentNode.route,
        );

      if (
        duplicateLabel ||
        (currentNode.type === 'item' && !trimmedRoute) ||
        duplicateRoute
      ) {
        return;
      }

      const visibility = values.disabled
        ? 'disabled'
        : values.hidden
          ? 'hidden'
          : (values.visibility ?? 'visible');

      const keywords = values.keywords
        ? values.keywords
            .split(',')
            .map((keyword) => keyword.trim())
            .filter(Boolean)
        : [];
      const permission = values.permission ? [values.permission] : [];
      const displayOrder = Number(values.displayOrder) || 1;
      const tooltip = values.tooltip?.trim() || undefined;

      const hasChanges =
        currentNode.label !== trimmedLabel ||
        (currentNode.type === 'item' && currentNode.route !== trimmedRoute) ||
        (currentNode.permissions?.[0] ?? 'standard-user') !==
          (values.permission ?? 'standard-user') ||
        (currentNode.keywords?.join(', ') ?? '') !== (values.keywords ?? '') ||
        (currentNode.tooltip ?? '') !== (tooltip ?? '') ||
        (currentNode.icon ?? 'apps') !== (values.icon ?? 'apps') ||
        currentNode.visibility !== visibility ||
        currentNode.displayOrder !== displayOrder ||
        (currentNode.openMode ?? 'sameTab') !== (values.openMode ?? 'sameTab');

      if (!hasChanges) {
        return;
      }

      onChangeRef.current({
        label: trimmedLabel,
        route: currentNode.type === 'item' ? trimmedRoute : undefined,
        permissions: permission,
        keywords,
        tooltip,
        icon: values.icon,
        visibility,
        displayOrder,
        openMode: values.openMode,
      });
    });

    return () => subscription.unsubscribe();
  }, [node?.id, watch]);

  if (isLoading) {
    return (
      <Box sx={{ px: navigationStudio.contentPadding, py: 2 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} height={52} sx={{ mb: 1.5, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (!node) {
    return (
      <Box
        sx={{
          px: navigationStudio.contentPadding,
          py: 3,
          bgcolor: navigationStudio.propertyPanelBg,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('navigationConfig.selectNodePrompt')}
        </Typography>
      </Box>
    );
  }

  const nodeTitle = resolveNodeLabel(node.label, t);

  return (
    <Box
      sx={{
        bgcolor: navigationStudio.propertyPanelBg,
        borderTop: 1,
        borderColor: navigationStudio.propertyPanelBorderTop,
      }}
    >
      <Box sx={{ px: navigationStudio.contentPadding, pt: 2.5, pb: 1.5 }}>
        <Typography
          variant="navSection"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('navigationConfig.nodePropertiesTitle', { node: nodeTitle })}
        </Typography>
      </Box>

      <Stack
        spacing={2.5}
        sx={{ px: navigationStudio.contentPadding, pb: navigationStudio.contentPadding }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              {...register('label')}
              label={t('navigationConfig.fields.navigationLabel')}
              size="small"
              fullWidth
              required
              error={Boolean(errors.label)}
              helperText={errors.label?.message}
            />
          </Grid>

          {node.type === 'item' ? (
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                {...register('route')}
                label={t('navigationConfig.fields.route')}
                size="small"
                fullWidth
                required
                error={Boolean(errors.route)}
                helperText={errors.route?.message}
              />
            </Grid>
          ) : null}

          <Grid size={{ xs: 12, md: node.type === 'item' ? 4 : 8 }}>
            <Controller
              name="permission"
              control={control}
              render={({ field }) => (
                <PermissionSelector
                  value={field.value}
                  onChange={field.onChange}
                  label={t('navigationConfig.fields.requiredPermission')}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              {...register('tooltip')}
              label={t('navigationConfig.fields.description')}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              {...register('displayOrder', { valueAsNumber: true })}
              label={t('navigationConfig.fields.displayOrder')}
              size="small"
              type="number"
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="navigation-icon-label">
                {t('navigationConfig.fields.icon')}
              </InputLabel>
              <Controller
                name="icon"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="navigation-icon-label"
                    label={t('navigationConfig.fields.icon')}
                  >
                    {navigationIconOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: navigationStudio.panelBorderColor,
                borderRadius: navigationStudio.panelBorderRadius,
                bgcolor: navigationStudio.panelBackground,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {t('navigationConfig.advancedTitle')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="navigation-visibility-label">
                        {t('navigationConfig.fields.visibility')}
                      </InputLabel>
                      <Controller
                        name="visibility"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="navigation-visibility-label"
                            label={t('navigationConfig.fields.visibility')}
                          >
                            {navigationVisibilityOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {t(option.labelKey)}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="navigation-open-mode-label">
                        {t('navigationConfig.fields.openMode')}
                      </InputLabel>
                      <Controller
                        name="openMode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="navigation-open-mode-label"
                            label={t('navigationConfig.fields.openMode')}
                          >
                            {navigationOpenModeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {t(option.labelKey)}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      {...register('keywords')}
                      label={t('navigationConfig.fields.searchKeywords')}
                      size="small"
                      fullWidth
                      placeholder={t(
                        'navigationConfig.fields.searchKeywordsPlaceholder',
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="hidden"
                          control={control}
                          render={({ field }) => (
                            <Switch {...field} checked={field.value} />
                          )}
                        />
                      }
                      label={t('navigationConfig.fields.hidden')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="disabled"
                          control={control}
                          render={({ field }) => (
                            <Switch {...field} checked={field.value} />
                          )}
                        />
                      }
                      label={t('navigationConfig.fields.disabled')}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('navigationConfig.futureFieldsNotice')}
        </Typography>
      </Stack>
    </Box>
  );
}
